<template>
  <Card variant="secondary" height="full" size="medium" class="lead-card">
    <View align="center" gap="sm">
      <Avatar :userName="renterName" />
      <View direction="column" justify="center">
        <Text variant="label-small">{{ renterName }}</Text>
        <Text variant="body-small" color="secondary">
          {{ showProperty ? propertyInquiry.property.displayName : leadSource }}
        </Text>
      </View>
      <InquiryStatusLabel :status="propertyInquiry.stage" :small="true" class="status" />
    </View>

    <Text variant="label-medium" as="div" v-if="monthlyIncome">
      {{ formatCurrency(monthlyIncome) }} total monthly income
    </Text>

    <div class="created-at">
      <View v-if="propertyInquiry.applicationRequestedAt" align="center" gap="xs">
        <span v-if="markWantsToApply && !propertyInquiry.applicationRequestIgnored" class="wants-to-apply-dot"></span>
        <Text variant="body-medium" color="tertiary">
          {{ propertyInquiry.applicationRequestIgnored ? 'Request ignored' : 'Wants to apply' }} · asked
          {{ useDateFormat(propertyInquiry.applicationRequestedAt, 'MMM D') }}
        </Text>
      </View>
      <Text variant="body-medium" v-else-if="propertyInquiry.createdAt" color="tertiary">
        Inquired {{ useDateFormat(propertyInquiry.createdAt, 'MMM D') }}
      </Text>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import { computed } from 'vue';

import { formatCurrency, Card, Text, Avatar, View } from '@tallo/design-system';
import { InquiryStatusLabel, PropertyInquiryDto, PropertyInquirySource } from '@tallo/investor/property';

// showProperty names the unit in place of the lead source, for lists that span units. markWantsToApply adds a dot
// to the "Wants to apply" line, for lists that mix such renters with others: where every card says it, it's noise
const props = defineProps<{
  propertyInquiry: PropertyInquiryDto;
  showProperty?: boolean;
  markWantsToApply?: boolean;
}>();

const renterName = computed(() => props.propertyInquiry.renter.user.name);
const monthlyIncome = computed(() => props.propertyInquiry.renter.monthlyIncome);
const leadSource = computed(() => {
  if (props.propertyInquiry.source === PropertyInquirySource.ZILLOW) {
    return 'from Zillow';
  }

  if (props.propertyInquiry.source === PropertyInquirySource.FACEBOOK) {
    return 'from Facebook';
  }

  if (props.propertyInquiry.source === PropertyInquirySource.APPLY_LINK) {
    return 'from Apply Link';
  }

  return props.propertyInquiry.source;
});
</script>

<style scoped lang="scss">
.lead-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--t-spacing-md);
}

.created-at {
  margin-top: 2.25rem;
}

.status {
  align-self: start;
  margin-left: auto;
}

.wants-to-apply-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: var(--t-border-radius-half-rounded);
  background-color: var(--t-color-orange-500);
}
</style>
